/**
 * Minimal RFC 6901 JSON Pointer get/set for structured JSON documents.
 */

function decodeToken(token: string): string {
  return token.replace(/~1/g, '/').replace(/~0/g, '~');
}

function parsePointer(pointer: string): string[] {
  if (pointer === '') {
    return [];
  }
  if (!pointer.startsWith('/')) {
    throw new Error(
      `Invalid JSON Pointer: must be "" or start with "/" (got "${pointer}")`
    );
  }
  return pointer.slice(1).split('/').map(decodeToken);
}

/**
 * Returns the value at the given JSON Pointer, or undefined if the path is missing.
 */
export function jsonPointerGet(root: unknown, pointer: string): unknown {
  if (pointer === '') {
    return root;
  }
  const parts = parsePointer(pointer);
  let cur: unknown = root;
  for (const part of parts) {
    if (cur === null || typeof cur !== 'object') {
      return undefined;
    }
    if (Array.isArray(cur)) {
      const idx = parseInt(part, 10);
      if (String(idx) !== part || idx < 0 || idx >= cur.length) {
        return undefined;
      }
      cur = cur[idx];
    } else {
      const rec = cur as Record<string, unknown>;
      if (!(part in rec)) {
        return undefined;
      }
      cur = rec[part];
    }
  }
  return cur;
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * Replaces the element at the given JSON Pointer (must point to an array index) with
 * zero or more replacement values. Returns a deep-cloned root with the update.
 */
export function jsonPointerSplice(
  root: unknown,
  pointer: string,
  replacements: unknown[]
): unknown {
  if (pointer === '') {
    throw new Error('Cannot splice at root pointer ""');
  }
  const out = cloneJson(root) as Record<string, unknown> | unknown[];
  const parts = parsePointer(pointer);
  let cur: unknown = out;

  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]!;
    if (cur === null || typeof cur !== 'object') {
      throw new Error(
        `Cannot splice JSON Pointer "${pointer}": path segment "${part}" is not an object or array`
      );
    }
    if (Array.isArray(cur)) {
      const idx = parseInt(part, 10);
      if (String(idx) !== part || idx < 0 || idx >= cur.length) {
        throw new Error(
          `Cannot splice JSON Pointer "${pointer}": invalid array index "${part}"`
        );
      }
      cur = cur[idx];
    } else {
      const rec = cur as Record<string, unknown>;
      if (!(part in rec)) {
        throw new Error(
          `Cannot splice JSON Pointer "${pointer}": missing key "${part}"`
        );
      }
      cur = rec[part];
    }
  }

  const last = parts[parts.length - 1]!;
  if (!Array.isArray(cur)) {
    throw new Error(
      `Cannot splice JSON Pointer "${pointer}": parent of last segment must be an array`
    );
  }
  const idx = parseInt(last, 10);
  if (String(idx) !== last || idx < 0) {
    throw new Error(
      `Cannot splice JSON Pointer "${pointer}": last segment must be a non-negative integer array index`
    );
  }
  const deleteCount = idx < cur.length ? 1 : 0;
  cur.splice(idx, deleteCount, ...(replacements as never[]));
  return out;
}

/**
 * Sets the value at the given JSON Pointer. Returns a deep-cloned root with the update.
 * Pointer "" replaces the entire root.
 */
export function jsonPointerSet(
  root: unknown,
  pointer: string,
  value: unknown
): unknown {
  if (pointer === '') {
    return value;
  }
  const out = cloneJson(root) as Record<string, unknown> | unknown[];
  const parts = parsePointer(pointer);
  let cur: unknown = out;

  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]!;
    if (cur === null || typeof cur !== 'object') {
      throw new Error(
        `Cannot set JSON Pointer "${pointer}": path segment "${part}" is not an object or array`
      );
    }
    if (Array.isArray(cur)) {
      const idx = parseInt(part, 10);
      if (String(idx) !== part || idx < 0 || idx >= cur.length) {
        throw new Error(
          `Cannot set JSON Pointer "${pointer}": invalid array index "${part}"`
        );
      }
      cur = cur[idx];
    } else {
      const rec = cur as Record<string, unknown>;
      if (!(part in rec)) {
        throw new Error(
          `Cannot set JSON Pointer "${pointer}": missing key "${part}"`
        );
      }
      cur = rec[part];
    }
  }

  const last = parts[parts.length - 1]!;
  if (cur === null || typeof cur !== 'object') {
    throw new Error(
      `Cannot set JSON Pointer "${pointer}": parent is not an object or array`
    );
  }
  if (Array.isArray(cur)) {
    const idx = parseInt(last, 10);
    if (String(idx) !== last || idx < 0) {
      throw new Error(
        `Cannot set JSON Pointer "${pointer}": invalid array index "${last}"`
      );
    }
    if (idx >= cur.length) {
      cur.push(value as never);
    } else {
      cur[idx] = value as never;
    }
  } else {
    (cur as Record<string, unknown>)[last] = value;
  }
  return out;
}
